/** PBKDF2 + AES-GCM — backup .enc.json (v2). */
import {
  deriveAesGcmKey,
  PBKDF2_ITERATIONS,
  PBKDF2_ITERATIONS_LEGACY,
  PBKDF2_SALT_BYTES,
} from '../crypto/pbkdf2Kdf.ts'

const IV_LEN = 12

async function decryptWithIterations(
  data: Uint8Array,
  passphrase: string,
  iterations: number,
): Promise<Uint8Array> {
  const salt = data.slice(0, PBKDF2_SALT_BYTES)
  const iv = data.slice(PBKDF2_SALT_BYTES, PBKDF2_SALT_BYTES + IV_LEN)
  const ciphertext = data.slice(PBKDF2_SALT_BYTES + IV_LEN)
  const key = await deriveAesGcmKey(passphrase, salt as BufferSource, iterations)
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource,
  )
  return new Uint8Array(plain)
}

export async function encryptBackupPayload(plaintext: Uint8Array, passphrase: string): Promise<Uint8Array> {
  const salt = crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES))
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN))
  const key = await deriveAesGcmKey(passphrase, salt, PBKDF2_ITERATIONS)
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext as BufferSource),
  )
  const out = new Uint8Array(salt.length + iv.length + ciphertext.length)
  out.set(salt, 0)
  out.set(iv, salt.length)
  out.set(ciphertext, salt.length + iv.length)
  return out
}

export async function decryptBackupPayload(
  data: Uint8Array,
  passphrase: string,
  preferredIterations?: number,
): Promise<Uint8Array> {
  const minLen = PBKDF2_SALT_BYTES + IV_LEN + 16
  if (data.length < minLen) throw new Error('backup_too_short')

  const tryOrder: number[] = []
  if (preferredIterations && preferredIterations > 0) tryOrder.push(preferredIterations)
  if (!tryOrder.includes(PBKDF2_ITERATIONS)) tryOrder.push(PBKDF2_ITERATIONS)
  if (!tryOrder.includes(PBKDF2_ITERATIONS_LEGACY)) tryOrder.push(PBKDF2_ITERATIONS_LEGACY)

  let lastErr: unknown
  for (const iterations of tryOrder) {
    try {
      return await decryptWithIterations(data, passphrase, iterations)
    } catch (err) {
      lastErr = err
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('backup_decrypt_failed')
}

export { PBKDF2_ITERATIONS, PBKDF2_SALT_BYTES }
